import React from "react";

import { Title } from "~/components";
import { usePageTitle } from "~/shared";

export const ConsumerHealthData = () => {
  usePageTitle("Consumer Health Data Privacy Notice");

  return (
    <main className="min-h-screen bg-gray-50 py-4 md:py-8">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="mb-6 text-center">
          <Title className="mb-2 text-2xl font-bold text-gray-900 md:text-3xl">
            Elektra Health Consumer Health Data Privacy Notice
          </Title>
          <p className="text-sm text-gray-500">
            <strong>Last modified:</strong> September 15, 2026
          </p>
        </div>

        <div className="space-y-8">
          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              Introduction
            </h2>
            <p className="mb-4 text-gray-700">
              This Consumer Health Data Privacy Notice ("Notice") provides
              information regarding the consumer health data that we collect,
              how we use it, what sources it is derived from, and to whom we
              disclose it, and provides information regarding rights of
              individuals who are residents of Connecticut, Nevada, or
              Washington.
            </p>
            <p className="mb-4 text-gray-700">
              This Notice is for Connecticut, Nevada, and Washington residents
              about whom we collect consumer health data, and is intended to
              provide notices in compliance with the Connecticut Data Privacy
              Act, Nevada's SB 370, and Washington's My Health, My Data Act. The
              provisions contained within this Notice are intended to provide
              notices in compliance with the Connecticut Data Privacy Act,
              Nevada's SB 370, and Washington's My Health, My Data Act.
            </p>
            <p className="mb-4 text-gray-700">
              Please note that we are providing the following disclosures and
              rights in this Notice in the interest of transparency. Such
              disclosures and rights are not intended to waive any applicable
              exemptions under state and federal law.
            </p>
            <p className="mb-4 text-gray-700">
              We encourage you to review our{" "}
              <a
                href="/consent/privacy-policy"
                className="text-blue-600 underline"
              >
                Privacy Policy
              </a>{" "}
              to learn more about our privacy practices. This Notice
              incorporates our Terms of Use and Privacy Policy.
            </p>
            <p className="text-gray-700">
              For the purposes of this Notice, "consumer health data" means
              personal information that is linked or reasonably linkable to you
              and that identifies your past, present, or future health status or
              mental health status, as may be applicable.
            </p>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              1. Consumer Health Data We May Collect
            </h2>
            <p className="mb-4 text-gray-700">
              We may collect, or have collected, the following categories of
              consumer health data about you:
            </p>
            <ul className="ml-6 list-disc text-gray-700">
              <li>
                Individual health treatments, conditions, treatment, diseases,
                or diagnosis
              </li>
              <li>
                Social, psychological, behavioral, or medical interventions
              </li>
              <li>Health-related surgeries or procedures</li>
              <li>Use or purchase of prescribed medication</li>
              <li>Diagnosis or diagnostic testing, treatment, or medication</li>
              <li>Gender-affirming care information</li>
              <li>Reproductive or sexual health information</li>
              <li>Bodily functions</li>
              <li>Biometric data</li>
              <li>
                Vital signs, symptoms, or measurements of the above categories
              </li>
              <li>
                Precise location information that could reasonably indicate your
                attempt to acquire or receive health services or supplies
              </li>
              <li>Data that identifies you seeking health care services</li>
              <li>
                Any information that we or our service providers process to
                associate or identify you with the above information that is
                derived from non-health information (such as inferred data)
              </li>
            </ul>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              2. Sources of Consumer Health Data
            </h2>
            <p className="mb-4 text-gray-700">
              We may collect consumer health data from the following categories
              of sources:
            </p>
            <ul className="ml-6 list-disc text-gray-700">
              <li>
                <strong>Directly from you,</strong> through your interactions
                with us, such as when you use our Site or Services, complete
                electronic forms, or otherwise contact us via chat, email, or
                phone.
              </li>
              <li>
                <strong>From third-party service providers,</strong> in
                accordance with applicable law and the context in which you
                provided the data.
              </li>
              <li>
                <strong>From the Practice and/or Practitioners</strong> who
                provide treatment and other services to you in connection with
                the Services.
              </li>
            </ul>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              3. How We Use Consumer Health Data
            </h2>
            <p className="mb-4 text-gray-700">
              We use the consumer health data we collect about you:
            </p>
            <ul className="ml-6 list-disc text-gray-700">
              <li>
                To provide, operate, maintain, improve, personalize, and expand
                our Services.
              </li>
              <li>
                To communicate with you, including to provide customer service
                and provide you with updates about our Services.
              </li>
              <li>To perform internal analytics, research, and development.</li>
              <li>For marketing and promotional purposes.</li>
              <li>
                For internal business purposes, including general business
                administration.
              </li>
              <li>
                For systems and data security, including to prevent
                unauthorized, improper, or illegal activities on our Services.
              </li>
              <li>
                To comply with our legal obligations, including responding to
                subpoenas, court orders, or legal process; and to establish or
                exercise our legal rights or defenses against legal claims.
              </li>
              <li>
                To enforce the legal or contractual terms that govern our
                Services.
              </li>
              <li>
                To evaluate or conduct a merger, divestiture, restructuring,
                reorganization, dissolution, or other sale or transfer of some
                or all of our assets, whether as a going concern or as part of
                bankruptcy, liquidation, or similar proceeding, in which
                personal data held by us about the users of our Services is
                among the assets transferred.
              </li>
              <li>
                For purposes disclosed at the time you provide your information,
                with your consent, as otherwise set forth in this Notice, or for
                any other legal purpose not inconsistent with this Notice.
              </li>
            </ul>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              4. To Whom We Disclose Consumer Health Data
            </h2>
            <p className="mb-4 text-gray-700">
              We may disclose your consumer health data to the following
              parties, in accordance with applicable law and the context in
              which you provided the information:
            </p>
            <ul className="mb-4 ml-6 list-disc text-gray-700">
              <li>
                Our third party service providers ("Vendors") that provide
                services to us to enable us to provide the Services, such as the
                hosting of the Services, data analysis, IT services and
                infrastructure, customer service, email delivery, and other
                similar services;
              </li>
              <li>
                Our Vendors that provide services to enable us to run our
                business and administrative operations, such as legal and
                financial advisory services, auditing services, and other
                similar services;
              </li>
              <li>
                The Practice, its Practitioners, and pharmacies to enable them
                to provide services to you via the Services and to collect
                payment on their behalf;
              </li>
              <li>To researchers, to permit them to study women's health;</li>
              <li>
                Business transferees, if we are involved in a merger,
                acquisition, asset sale, or other corporate combination; and
              </li>
              <li>Other third parties with your consent.</li>
            </ul>
            <p className="text-gray-700">
              Under certain circumstances, we may disclose your data if required
              to do so by law, in response to valid requests by public
              authorities, or in response to a threat of harm involving an
              individual's health or safety.
            </p>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              5. Your Consumer Health Data Privacy Rights
            </h2>
            <p className="mb-4 text-gray-700">
              Connecticut residents have the following rights in relation to
              your consumer health data, subject to certain exceptions:
            </p>
            <ul className="mb-4 ml-6 list-disc text-gray-700">
              <li>
                <strong>Right to know and access.</strong> You have the right to
                know what consumer health data we process, as the term is
                defined under applicable law. You may ask us to provide you with
                a portable copy of this information.
              </li>
              <li>
                <strong>Right to delete.</strong> You have the right to request
                under certain circumstances that we, as well as our service
                providers and contractors, delete the consumer health data that
                we collect about you.
              </li>
              <li>
                <strong>
                  Right to correct inaccurate consumer health data.
                </strong>{" "}
                You have the right to request the correction of inaccurate
                consumer health data.
              </li>
              <li>
                <strong>Right to opt out.</strong> You have the right to opt out
                of targeted advertising and profiling decisions that could
                produce legal or similarly significant effects.
              </li>
              <li>
                <strong>Rights concerning sensitive personal data.</strong> We
                cannot process your consumer health data, or use your consumer
                health data for certain purposes without your affirmative
                consent.
              </li>
              <li>
                <strong>Right to non-discrimination.</strong> You have the right
                not to receive discriminatory treatment for the exercise of the
                privacy rights described above.
              </li>
            </ul>
            <p className="mb-4 text-gray-700">
              Nevada and Washington residents have the following rights in
              relation to your consumer health data, subject to certain
              exceptions:
            </p>
            <ul className="mb-4 ml-6 list-disc text-gray-700">
              <li>
                <strong>Right to know.</strong> You have the right to know what
                consumer health data we collect or share, as those terms are
                defined under applicable law. You also have the right to obtain
                a list of all third parties and affiliates with whom we have
                shared or sold your consumer health data, and an active email
                address or other mechanism that you may use to contact these
                third parties. If you are a Washington resident or otherwise
                protected by Washington law, you also have the right to access
                your consumer health data that we collect or share.
              </li>
              <li>
                <strong>Right to withdraw consent.</strong> You have the right
                to withdraw consent from the collection and sharing of your
                consumer health data.
              </li>
              <li>
                <strong>Right to delete.</strong> You have the right to request
                that we, as well as our service providers and contractors,
                delete the consumer health data that we collect about you.
              </li>
              <li>
                <strong>Right to non-discrimination.</strong> You have the right
                not to receive discriminatory treatment for the exercise of the
                privacy rights described above.
              </li>
            </ul>
            <p className="text-gray-700">
              You may exercise any of the rights described above by emailing
              care@elektrahealth.com.
            </p>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              6. How to Appeal Decisions About Your Rights
            </h2>
            <p className="mb-4 text-gray-700">
              Residents of Connecticut, Nevada, and Washington can appeal
              decisions about privacy rights in the following ways:
            </p>
            <div className="space-y-3 text-gray-700">
              <p>
                <strong>Connecticut.</strong> If you are a Connecticut resident
                and want to appeal our decision regarding a request that you
                have made, please contact us. Within sixty (60) days of receipt
                of an appeal, we will inform you in writing of any action taken
                or not taken, including an explanation of our reasons for
                reaching the decision. If the appeal is denied, you may contact
                Connecticut's Office of the Attorney General by phone at (860)
                808-5420 or by submitting a form{" "}
                <a
                  href="https://portal.ct.gov/AG/Common/Complaint-Form-Landing-page"
                  className="text-blue-600 underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </a>
                .
              </p>
              <p>
                <strong>Nevada.</strong> If you are a Nevada resident and want
                to appeal our decision regarding a request that you have made,
                please contact us. Within forty-five (45) days of receipt of an
                appeal, we will inform you in writing of any action taken or not
                taken, including an explanation of our reasons for reaching the
                decision. If your appeal is not successful, you may submit a
                complaint with the Nevada Attorney General{" "}
                <a
                  href="https://ag.nv.gov/Complaints/File_Complaint/"
                  className="text-blue-600 underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </a>
                .
              </p>
              <p>
                <strong>Washington.</strong> If you are a Washington resident or
                located in Washington and want to appeal our decision regarding
                a request that you have made, please contact us. Within
                forty-five (45) days of receipt of an appeal, we will inform you
                in writing of any action taken or not taken, including an
                explanation of our reasons for reaching the decision. If the
                appeal is denied, you may contact Washington's Office of the
                Attorney General by phone at (800) 551-4636 or by submitting a
                complaint{" "}
                <a
                  href="https://www.atg.wa.gov/file-complaint"
                  className="text-blue-600 underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </a>
                .
              </p>
            </div>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              7. Changes to this Notice
            </h2>
            <p className="text-gray-700">
              We may change this Notice from time to time to reflect new
              services or changes in our data practices or relevant laws. We
              will let you know via email and/or a prominent notice on our
              Sites, prior to the change becoming effective, and update
              "effective date" at the top of this Notice. We recommend
              periodically reviewing this Notice for any changes; changes are
              effective when they are posted on this page.
            </p>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-2xl font-semibold text-gray-900">
              8. Contacting Us
            </h2>
            <p className="mb-4 text-gray-700">
              To exercise your privacy rights, or if you have any questions
              about this Notice, email us at care@elektrahealth.com.
            </p>
            <p className="text-sm text-gray-500">
              <strong>Date last modified:</strong> September 15, 2026
            </p>
          </section>
        </div>
      </div>
    </main>
  );
};
